/**
 * GestorModulesDialog — admin-side drawer to toggle per-gestor module access.
 *
 * Open via the "Configurar módulos" button on Admin > Gestores.
 *  - Always-on modules (Wallet, Mis Pasajeros) are pinned and disabled.
 *  - Every change is sent to PATCH /api/admin/gestores/{id}/modules so the
 *    backend broadcasts `gestor.modules.changed` on the gestor's WS channel.
 *  - Optimistic UI: switch flips immediately; on failure we revert and toast.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GestorModulesDialog = ({ gestor, open, onOpenChange }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState({});
  const [alwaysOn, setAlwaysOn] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    if (!open || !gestor) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/admin/gestores/${gestor.id}/modules`, {
          headers: getAuthHeaders(),
        });
        if (cancelled) return;
        setFlags(r.data.enabled_modules || {});
        setAlwaysOn(r.data.always_on || []);
        setRegistry(r.data.registry || []);
      } catch (err) {
        toast.error('No se pudieron cargar los módulos del gestor');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, gestor, getAuthHeaders]);

  const toggle = async (key, next) => {
    const prev = flags[key];
    setFlags((f) => ({ ...f, [key]: next }));
    setSavingKey(key);
    try {
      await axios.patch(
        `${API_URL}/admin/gestores/${gestor.id}/modules`,
        { enabled_modules: { [key]: next } },
        { headers: getAuthHeaders() },
      );
      toast.success(`Módulo "${key}" ${next ? 'activado' : 'desactivado'}`, { duration: 1800 });
    } catch (err) {
      setFlags((f) => ({ ...f, [key]: prev }));
      toast.error(`No se pudo actualizar "${key}"`);
    } finally {
      setSavingKey(null);
    }
  };

  const previewAsGestor = async () => {
    try {
      const r = await axios.post(
        `${API_URL}/admin/gestores/${gestor.id}/preview-token`,
        {},
        { headers: getAuthHeaders() },
      );
      const url = `${window.location.origin}/preview/launch?token=${encodeURIComponent(r.data.token)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error('No se pudo abrir la vista previa');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="gestor-modules-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Configurar módulos
            {gestor?.name && <span className="text-amber-700 text-sm font-normal">— {gestor.name}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Activa o desactiva módulos para este gestor. Los cambios se aplican al instante vía WebSocket. Wallet y Mis Pasajeros están siempre activos.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Cargando módulos…
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            {registry.map((m) => {
              const pinned = alwaysOn.includes(m.key);
              const checked = pinned ? true : !!flags[m.key];
              return (
                <div
                  key={m.key}
                  className={`flex items-start justify-between gap-3 border rounded-lg p-3 ${pinned ? 'bg-slate-50' : 'bg-white'}`}
                  data-testid={`module-row-${m.key}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm text-slate-900">{m.label}</p>
                      {pinned && (
                        <Badge variant="secondary" className="text-[10px] flex items-center gap-1">
                          <Lock className="w-3 h-3" /> siempre activo
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{m.description}</p>
                  </div>
                  <Switch
                    checked={checked}
                    disabled={pinned || savingKey === m.key}
                    onCheckedChange={(v) => toggle(m.key, v)}
                    data-testid={`module-switch-${m.key}`}
                    aria-label={`Activar ${m.label}`}
                  />
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-4 flex sm:justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={previewAsGestor}
            data-testid="preview-as-gestor-btn"
            disabled={loading}
          >
            Vista previa como gestor
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)} data-testid="modules-close-btn">
            Listo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GestorModulesDialog;
